// Etapper i projektet: egen Bas-U, egen tidsperiod och egen överlämning per etapp.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import StageContractors from "./StageContractors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import { Layers, Plus, Trash2, ShieldCheck } from "lucide-react";
import StageDateShiftDialog from "./StageDateShiftDialog";
import { dayDiff } from "@/lib/basProcess/stageDates";

interface Props {
  projectId: string;
}

interface Stage {
  id: string;
  name: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  bas_u_name: string | null;
  bas_u_email: string | null;
  status: string;
  sort_order: number;
  amp_revision_required: boolean | null;
  amp_note: string | null;
  amp_reviewed_at: string | null;
  amp_reviewed_by: string | null;
  amp_version_frozen: number | null;
  closed_at: string | null;
  closed_by: string | null;
}

const STATUSES = ["planerad", "pågår", "avslutad"];

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString("sv-SE") : "–");

const ProjectStages: React.FC<Props> = ({ projectId }) => {
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState<{ stageId: string; stageName: string; days: number } | null>(
    null
  );
  const [form, setForm] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    bas_u_name: "",
    bas_u_email: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("project_stages")
      .select("*")
      .eq("project_id", projectId)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) toast.error("Kunde inte hämta etapperna");
    setStages((data || []) as Stage[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!form.name.trim()) {
      toast.error("Ge etappen ett namn");
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("project_stages").insert({
      project_id: projectId,
      name: form.name.trim(),
      description: form.description || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      bas_u_name: form.bas_u_name || null,
      bas_u_email: form.bas_u_email || null,
      sort_order: stages.length,
      created_by: auth.user?.id ?? null,
    });
    if (error) {
      toast.error("Kunde inte skapa etappen");
      return;
    }
    toast.success("Etappen är skapad");
    setForm({
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      bas_u_name: "",
      bas_u_email: "",
    });
    load();
  };

  const update = async (id: string, patch: Partial<Stage>) => {
    const { error } = await supabase.from("project_stages").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara ändringen");
    else load();
  };

  // Nya datum pa en etapp: erbjud att flytta kopplade ronder och möten lika mycket.
  const updateDate = async (s: Stage, field: "start_date" | "end_date", value: string) => {
    const previous = s[field];
    await update(s.id, { [field]: value || null } as Partial<Stage>);
    if (previous && value) {
      const days = dayDiff(previous, value);
      if (days !== 0) setShift({ stageId: s.id, stageName: s.name, days });
    }
  };


  // Stang en etapp: krav att AMP-granskning är bekräftad om amp_revision_required är ikryssat.
  const closeStage = async (s: Stage) => {
    if (s.amp_revision_required && !s.amp_reviewed_at) {
      toast.error("AMP-revidering krävs för denna etapp – bekräfta granskningen först.");
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const nowIso = new Date().toISOString();
    const { error } = await supabase
      .from("project_stages")
      .update({
        status: "avslutad",
        closed_at: nowIso,
        closed_by: auth.user?.id ?? null,
      })
      .eq("id", s.id);
    if (error) {
      toast.error("Kunde inte stänga etappen");
      return;
    }
    // Logga i sakerhetsprotokollet
    await supabase.from("security_audit_log").insert({
      action_type: "stage_closed",
      resource_type: "project_stage",
      resource_id: s.id,
      details: {
        stage_name: s.name,
        amp_version: s.amp_version_frozen,
        closed_by: auth.user?.id ?? null,
      },
    });
    toast.success(`Etapp "${s.name}" stängd`);
    load();
  };

  // Bekrafta AMP-granskning för en etapp.
  const confirmAmpReview = async (s: Stage) => {
    const { data: auth } = await supabase.auth.getUser();
    const { data: versions } = await supabase
      .from("amp_versions")
      .select("version")
      .eq("project_id", projectId)
      .order("version", { ascending: false })
      .limit(1);
    const latestVersion = versions?.[0]?.version ?? null;
    const { error } = await supabase
      .from("project_stages")
      .update({
        amp_reviewed_at: new Date().toISOString(),
        amp_reviewed_by: auth.user?.id ?? null,
        amp_version_frozen: latestVersion,
      })
      .eq("id", s.id);
    if (error) {
      toast.error("Kunde inte bekräfta AMP-granskning");
      return;
    }
    toast.success("AMP-granskning bekräftad – version fryst för etappen");
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("project_stages").delete().eq("id", id);
    if (error) toast.error("Kunde inte ta bort etappen");
    else {
      toast.success("Etappen är borttagen");
      load();
    }
  };

  const missingBasU = stages.filter((s) => !s.bas_u_name && !s.bas_u_email);

  return (
    <div className="space-y-4">
      <DocumentHeader
        icon={Layers}
        title="Etapper"
        description="Dela upp projektet i etapper när olika delar har olika tider och olika Bas-U. Varje etapp får egen period, egen ansvarig och egen status."
        legalRef="AFS 2023:3"
        meta={
          <Badge variant={missingBasU.length ? "destructive" : "secondary"}>
            {stages.length} etapper
            {missingBasU.length ? ` · ${missingBasU.length} utan Bas-U` : ""}
          </Badge>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ny etapp</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Namn</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="T.ex. Etapp 1 – rivning och sanering"
            />
          </div>
          <div>
            <Label>Start</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
          </div>
          <div>
            <Label>Slut</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            />
          </div>
          <div>
            <Label>Bas-U för etappen</Label>
            <Input
              value={form.bas_u_name}
              onChange={(e) => setForm({ ...form, bas_u_name: e.target.value })}
              placeholder="Namn"
            />
          </div>
          <div>
            <Label>E-post till Bas-U</Label>
            <Input
              type="email"
              value={form.bas_u_email}
              onChange={(e) => setForm({ ...form, bas_u_email: e.target.value })}
              placeholder="namn@foretag.se"
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Beskrivning</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Vad omfattar etappen och vilka risker är särskilda för den?"
            />
          </div>
          <div className="sm:col-span-2">
            <Button onClick={add}>
              <Plus className="mr-1 h-4 w-4" /> Lägg till etapp
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Etapper i projektet</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
          {!loading && stages.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Inga etapper ännu. Små projekt klarar sig utan – lägg bara till etapper när delarna
              har olika tider eller olika Bas-U.
            </p>
          )}
          {stages.map((s) => (
            <div key={s.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {fmt(s.start_date)} – {fmt(s.end_date)} ·{" "}
                    {s.bas_u_name || s.bas_u_email || "Bas-U saknas"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={s.status} onValueChange={(v) => update(s.id, { status: v })}>
                    <SelectTrigger className="w-full sm:w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((st) => (
                        <SelectItem key={st} value={st}>
                          {st}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => remove(s.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">Start</Label>
                  <Input
                    type="date"
                    defaultValue={s.start_date ?? ""}
                    onChange={(e) => updateDate(s, "start_date", e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Slut</Label>
                  <Input
                    type="date"
                    defaultValue={s.end_date ?? ""}
                    onChange={(e) => updateDate(s, "end_date", e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  Ändrar du datum får du välja om skyddsronder och samordningsmöten i etappen ska
                  flyttas lika många dagar.
                </p>
              </div>
              {s.description && <p className="mt-2 text-sm">{s.description}</p>}
              {!s.bas_u_name && !s.bas_u_email && (
                <p className="mt-2 text-sm text-destructive">
                  Etappen saknar utsedd Bas-U – ansvarsluckan syns vid tillsyn.
                </p>
              )}
              <div className="mt-3 space-y-2 rounded-md bg-muted/40 p-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-current"
                    checked={!!s.amp_revision_required}
                    onChange={(e) => update(s.id, { amp_revision_required: e.target.checked })}
                  />
                  Arbetsmiljöplanen behöver revideras för den här etappen
                </label>
                <Textarea
                  rows={2}
                  defaultValue={s.amp_note || ""}
                  onBlur={(e) => update(s.id, { amp_note: e.target.value || null })}
                  placeholder="AMP-bilaga för etappen: vad som skiljer, vilka avsnitt som ändras och vilken AMP-version som gäller."
                />
                {s.amp_revision_required && !s.amp_reviewed_at && (
                  <p className="text-xs text-destructive">
                    Frys en ny AMP-version innan etappen startar, så går det att visa vilken plan
                    som gällde när. Bekräfta granskningen nedan innan etappen kan stängas.
                  </p>
                )}
                {s.amp_reviewed_at && (
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                      AMP-granskad {fmt(s.amp_reviewed_at)}
                      {s.amp_version_frozen != null ? ` · v${s.amp_version_frozen}` : ""}
                    </Badge>
                  </div>
                )}
                {s.amp_revision_required && !s.amp_reviewed_at && (
                  <Button size="sm" variant="outline" onClick={() => confirmAmpReview(s)}>
                    <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Bekräfta AMP-granskning
                  </Button>
                )}
                {s.status !== "avslutad" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => closeStage(s)}
                    className={s.amp_revision_required && !s.amp_reviewed_at ? "opacity-50" : ""}
                  >
                    Stäng etapp
                  </Button>
                ) : (
                  <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                    Stängd {s.closed_at ? fmt(s.closed_at) : ""}
                  </Badge>
                )}
              </div>

              <div className="mt-3">
                <StageContractors projectId={projectId} stageId={s.id} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <StageDateShiftDialog
        open={!!shift}
        stageId={shift?.stageId ?? null}
        stageName={shift?.stageName ?? ""}
        days={shift?.days ?? 0}
        onClose={() => setShift(null)}
        onDone={load}
      />
    </div>
  );
};

export default ProjectStages;
